import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faEye,
  faTrash,
  faFilter,
  faClock,
  faCheckCircle,
  faSpinner,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import { useCustomLoading } from "../hooks/useCustomLoading";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

/**
 * RepairProjects Page
 *
 * Lists all span-based repair projects with:
 * - Pagination
 * - Filters (bridge, status, priority, year)
 * - Quick actions (view, edit, delete)
 * - Status indicators
 */
const RepairProjects = () => {
  const navigate = useNavigate();
  const { withLoading } = useCustomLoading();

  const [projects, setProjects] = useState([]);
  const [bridges, setBridges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  });

  // Filters
  const [filters, setFilters] = useState({
    bridgeId: "",
    status: "",
    priority: "",
    plannedYear: "",
  });

  useEffect(() => {
    fetchBridges();
  }, []);

  const fetchBridges = async () => {
    try {
      const response = await api.get("/bridges?limit=100");
      if (response.data.success) {
        setBridges(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching bridges:", err);
    }
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.itemsPerPage.toString(),
      });

      if (filters.bridgeId) params.append("bridge_id", filters.bridgeId);
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.plannedYear)
        params.append("planned_year", filters.plannedYear);

      const response = await api.get(`/repair-projects?${params.toString()}`);

      if (response.data.success) {
        setProjects(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to fetch repair projects");
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.itemsPerPage, filters]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 })); // Reset to page 1
  };

  const clearFilters = () => {
    setFilters({
      bridgeId: "",
      status: "",
      priority: "",
      plannedYear: "",
    });
  };

  const deleteProject = async (projectId, projectName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete repair project "${projectName}"?`,
      )
    ) {
      return;
    }

    try {
      await withLoading(async () => {
        await api.delete(`/repair-projects/${projectId}`);
        fetchProjects();
      }, "Deleting repair project...");
    } catch (err) {
      console.error("Error deleting project:", err);
      alert("Failed to delete repair project");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return faClock;
      case "completed":
        return faCheckCircle;
      default:
        return faClock;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="mt-1 flex items-start gap-2">
          <p className="text-gray-600">Span-based repair project management</p>
          <InlineFieldTooltip
            page="repair_projects"
            section="page_description"
            fieldKey="repair_projects_overview"
          />
        </div>
        <button
          onClick={() => navigate("/repair-projects/create")}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} />
          Create New Project
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faFilter} />
              Filters
            </h2>
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Bridge Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bridge
              </label>
              <select
                value={filters.bridgeId}
                onChange={(e) => handleFilterChange("bridgeId", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Bridges</option>
                {bridges.map((bridge) => (
                  <option key={bridge.id} value={bridge.id}>
                    {bridge.bridgeName}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange("priority", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Year Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Planned Year
              </label>
              <input
                type="number"
                value={filters.plannedYear}
                onChange={(e) =>
                  handleFilterChange("plannedYear", e.target.value)
                }
                placeholder="Year..."
                min="2020"
                max="2040"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="text-red-600 mr-3"
            />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Projects Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Projects</h2>
            <div className="text-sm text-gray-600">
              {pagination.totalItems} total project
              {pagination.totalItems !== 1 ? "s" : ""}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className="text-4xl text-gray-400 mb-4"
            />
            <p className="text-gray-600">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">No repair projects found</p>
            <button
              onClick={() => navigate("/repair-projects/create")}
              className="mt-4 text-blue-600 hover:text-blue-800"
            >
              Create your first project
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bridge
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Spans
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {projects.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/repair-projects/${project.id}`)
                            }
                            className="text-sm font-medium text-blue-700 transition hover:text-blue-900 hover:underline"
                            title="Open project details"
                          >
                            {project.project_name}
                          </button>
                          {project.project_code && (
                            <div className="text-xs text-gray-500">
                              {project.project_code}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {project.bridge_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {project.span_count || 0} span
                        {(project.span_count || 0) !== 1 ? "s" : ""}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {project.planned_year}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}
                        >
                          <FontAwesomeIcon
                            icon={getStatusIcon(project.status)}
                            className="mr-1"
                          />
                          {project.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(project.priority)}`}
                        >
                          {project.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex space-x-2">
                          {project.status !== "completed" && (
                            <button
                              onClick={() =>
                                navigate(`/repair-projects/${project.id}/edit`)
                              }
                              className="text-emerald-600 hover:text-emerald-900"
                              title="Edit Project"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          )}
                          <button
                            onClick={() =>
                              navigate(`/repair-projects/${project.id}`)
                            }
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            onClick={() =>
                              deleteProject(project.id, project.project_name)
                            }
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          currentPage: prev.currentPage - 1,
                        }))
                      }
                      disabled={!pagination.hasPrevPage}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          currentPage: prev.currentPage + 1,
                        }))
                      }
                      disabled={!pagination.hasNextPage}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RepairProjects;
